import { Box, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ContentState, convertToRaw, EditorState, Modifier } from 'draft-js';
import { announcementMessageStyles } from './CustomStyles';
import { PersonalizeWysiwygOption } from './PersonalizeWysiwygOption';
import { AnnouncementPersonalizeModal } from './AnnouncementPersonalizeModal';
import { PersonalizePopupToolbar } from './PersonalizePopupToolbar';
import { AnnouncementState, selectAnnouncementsState, updateAnnouncement } from '../sagas/announcementSlice';
import { useDispatch, useSelector } from 'react-redux';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import { AnnouncementPersonalizationDto } from '@inosio/payments-sdk';
import { extractPlaceholderKeysFromTheText, filterDistinctPersonalizations } from './utils/utils';
import { AnnouncementSectionProps } from '../validators/AnnouncementValidators';

const headerHeight = 84;
const EditorToolbar = {
  options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'remove', 'history'],
  inline: {
    options: ['bold', 'italic', 'underline', 'strikethrough'],
  },
};

interface IProps extends AnnouncementSectionProps {}

export const AnnouncementMessage = (props: IProps) => {
  const dispatch = useDispatch();

  const { announcementDetail }: AnnouncementState = useSelector(selectAnnouncementsState);
  const [openAnnouncementPersonalizeModal, setOpenAnnouncementPersonalizeModal] = useState<boolean>(false);
  const [isEditingPlaceholder, setEditingPlaceholder] = useState<boolean>(false);
  const classes = announcementMessageStyles();
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const editorRef = useRef<any>();
  const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showToolbar, setShowToolbar] = useState<boolean>(false);
  const [selectedPersonalizedField, setSelectedPersonalizedField] = useState<AnnouncementPersonalizationDto>();

  const onSelectPersonalizeField = (personalization: AnnouncementPersonalizationDto) => {
    const personalizations = announcementDetail.Personalizations || [];

    const updatedPersonalizations = personalizations.some((f) => f.Key === personalization.Key)
      ? personalizations.map((f) => (f.Key === personalization.Key ? personalization : f))
      : [...personalizations, personalization];
    if (!isEditingPlaceholder) {
      const selection = editorState.getSelection();
      const contentState = editorState.getCurrentContent();

      // Insert the personalized field key into the editor at the current selection
      const newContentState = Modifier.replaceText(contentState, selection, personalization.Key, editorState.getCurrentInlineStyle());
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
      onHandleAnnouncementModalClose();

      const currentContent = newEditorState.getCurrentContent();
      const rawContent = convertToRaw(currentContent);
      const htmlContent = draftToHtml(rawContent);
      dispatch({
        type: updateAnnouncement.type,
        payload: {
          uuid: announcementDetail.TemplateUuid,
          updateAnnouncementDto: {
            Body: htmlContent,
            Personalizations: filterDistinctPersonalizations(announcementDetail.Subject, htmlContent, updatedPersonalizations),
          },
        },
      });
    } else {
      props.setPendingAnnouncement({
        Personalizations: filterDistinctPersonalizations(announcementDetail.Subject, announcementDetail.Body, updatedPersonalizations),
      });
      onHandleAnnouncementModalClose();
    }
  };

  const handleEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    handleSelectionChange(newEditorState);
    const currentContent = newEditorState.getCurrentContent();
    const rawContent = convertToRaw(currentContent);
    const htmlContent = draftToHtml(rawContent);
    props.setPendingAnnouncement({
      Body: htmlContent,
    });
  };

  const handleSelectionChange = (newEditorState: EditorState) => {
    const selection = newEditorState.getSelection();
    const contentState = newEditorState.getCurrentContent();
    const windowSelection = window.getSelection();

    if (windowSelection && !selection.isCollapsed()) {
      const selectedText = contentState.getBlockForKey(selection.getStartKey()).getText().slice(selection.getStartOffset(), selection.getEndOffset());
      const keys = extractPlaceholderKeysFromTheText(selectedText);
      const range = windowSelection?.rangeCount > 0 ? windowSelection?.getRangeAt(0) : null;

      if (range && keys.length > 0) {
        const selectedFieldKey = keys[0];
        const rect = range.getBoundingClientRect();
        const centerTop = (rect.top + rect.bottom) / 2;

        setSelectedPersonalizedField(announcementDetail?.Personalizations?.find((field) => field.Key === selectedFieldKey));
        setShowToolbar(true);
        setToolbarPosition({
          top: centerTop + window.scrollY - (50 + headerHeight),
          left: rect.left + window.scrollX - 150,
        });
      }
    } else {
      const cursorPosition = selection.getAnchorOffset();
      const blockKey = selection.getStartKey();
      const blockText = contentState.getBlockForKey(blockKey).getText();
      const match = blockText.slice(0, cursorPosition).match(/{{([a-z_]+)}}$/);

      if (match) {
        const placeholderKey = match[0];
        const placeholder = announcementDetail?.Personalizations?.find((field) => field.Key === placeholderKey);

        if (placeholder && windowSelection) {
          setSelectedPersonalizedField(placeholder);

          const blockElement = editorRef.current?.querySelector(`[data-offset-key="${blockKey}-0-0"]`);
          if (blockElement) {
            const spanWithDataTextTrue = blockElement.querySelector('span[data-text="true"]');
            if (spanWithDataTextTrue) {
              const spanText = spanWithDataTextTrue.textContent;
              const placeholderIndex = spanText?.indexOf(placeholderKey);

              if (placeholderIndex !== -1) {
                const range = document.createRange();
                range.setStart(spanWithDataTextTrue.firstChild, placeholderIndex);
                range.setEnd(spanWithDataTextTrue.firstChild, placeholderIndex + placeholderKey.length);

                const rect = range.getBoundingClientRect();
                const centerTop = (rect.top + rect.bottom) / 2;
                setShowToolbar(true);
                setToolbarPosition({
                  top: centerTop + window.scrollY - (50 + headerHeight),
                  left: rect.left + window.scrollX - 150,
                });
              } else {
                setShowToolbar(false);
                setSelectedPersonalizedField(undefined);
              }
            } else {
              setShowToolbar(false);
              setSelectedPersonalizedField(undefined);
            }
          }
        }
      } else {
        setShowToolbar(false);
        setSelectedPersonalizedField(undefined);
      }
    }

    setEditorState(newEditorState);
  };

  const onHandleAnnouncementModalClose = () => {
    setOpenAnnouncementPersonalizeModal(false);
    setSelectedPersonalizedField(undefined);
    setEditingPlaceholder(false);
  };

  useEffect(() => {
    if (announcementDetail.Body) {
      const html = announcementDetail.Body;
      const contentBlock = htmlToDraft(html);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
      }
    }
  }, []);

  return (
    <Box sx={{ padding: '20px' }}>
      <Grid container gap={5}>
        <Grid container>
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: '20px' }}>Message:</Typography>
          </Grid>
          <Grid item xs={12}>
            <Editor
              editorRef={(ref) => {
                editorRef.current = ref;
              }}
              editorState={editorState}
              toolbarClassName={classes.toolbarClass}
              wrapperClassName={classes.wrapperClass}
              editorClassName={classes.editorClass}
              onEditorStateChange={handleEditorStateChange}
              toolbar={EditorToolbar}
              toolbarCustomButtons={[<PersonalizeWysiwygOption onClick={() => setOpenAnnouncementPersonalizeModal(true)} />]}
            />
            <PersonalizePopupToolbar
              show={showToolbar}
              selectedPersonalizedField={selectedPersonalizedField}
              position={toolbarPosition}
              onEdit={() => {
                setEditingPlaceholder(true);
                setShowToolbar(false);
                setOpenAnnouncementPersonalizeModal(true);
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <AnnouncementPersonalizeModal
        title="Personalize Message"
        open={openAnnouncementPersonalizeModal}
        onClose={onHandleAnnouncementModalClose}
        onSelectPersonalizeField={onSelectPersonalizeField}
        defaultPeronalizeField={selectedPersonalizedField}
      />
    </Box>
  );
};
